import { ColorCodeContainer, Wrapper, WrapperColumn, WrapperRow } from 'components/auditorium/auditorium.style'
import { AuditoriumSelling } from 'components/auditorium/selling'
import { ActionButton } from 'components/button/button.styled'
import ColorCode from 'components/colorCode'
import { ModeSelection } from 'components/modeSelection/modeSelection'
import ComplimentaryCheckbox from 'components/seatSelector/components/complimentaryCheckbox'
import { SeatSummary } from 'components/seatSelector/components/seatSummary'
import { SmallTitle, SummaryRow, TicketPrice, Title } from 'components/seatSelector/seatSelector.style'
import { useToken } from 'hooks/token'
import { useReservationTimer } from 'hooks/user/useTickets/useReservationTimer'
import { useTicketsLockedByUser } from 'hooks/user/useTickets/useTicketsLockedByUser'
import { Row } from 'primereact/row'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { EventDetails } from 'types/Event'

const COLOR_CODES = [
    { color: '#00AC11', label: 'Elérhető ülőhelyek' },
    { color: '#F4C000', label: 'Ön által kiválasztott ülőhelyek' },
    { color: '#CE0000', label: 'Nem elérhető ülőhelyek' },
]

interface Props {
    currentEvent: EventDetails
    selectedSeats: string[]
    changeSelectedSeats: (seats: string[]) => void
    totalPrice: number
    actionLabel: string
    actionHandler: (selectedSeats: string[]) => void
    complimentaryButton?: boolean
    isComplimentary?: boolean
    setIsComplimentary?: Dispatch<SetStateAction<boolean>>
}

export const SeatBasedSelling: React.FC<Props> = ({
    currentEvent,
    selectedSeats,
    changeSelectedSeats,
    totalPrice,
    actionLabel,
    actionHandler,
    complimentaryButton = false,
    isComplimentary,
    setIsComplimentary,
}) => {
    const eventId = currentEvent.event_id
    const [auditoriumViewMode, setAuditoriumViewMode] = useState<'floor' | 'gallery'>('floor')
    const { sessionId } = useToken()
    const { data: ticketsLockedByUser } = useTicketsLockedByUser(eventId, sessionId ?? '')
    const { formatted: reservationTime } = useReservationTimer(ticketsLockedByUser)

    const galleryExists = !!currentEvent.location.auditorium?.gallery

    // Determine which auditorium data to show
    const isGallery = auditoriumViewMode === 'gallery'
    const auditoriumData = isGallery ? currentEvent.location.auditorium.gallery : currentEvent.location.auditorium

    return (
        <>
            <Title>Válassza ki ülőhelyét!</Title>
            <WrapperRow style={{ justifyContent: 'space-around', gap: '2rem' }}>
                <WrapperColumn>
                    <Wrapper>
                        {galleryExists ? (
                            <ModeSelection
                                options={[
                                    { value: 'floor', label: 'Földszint' },
                                    { value: 'gallery', label: currentEvent.location.auditorium.gallery?.gallery_name || 'Karzat' },
                                ]}
                                selectedValue={auditoriumViewMode}
                                onChange={setAuditoriumViewMode}
                                buttonClassName="my-button"
                                selectedButtonClassName="selected"
                            />
                        ) : null}
                    </Wrapper>

                    {auditoriumData && (
                        <AuditoriumSelling
                            key={galleryExists ? auditoriumViewMode : 'floor'}
                            rows={auditoriumData.rows}
                            columns={auditoriumData.columns}
                            seats={auditoriumData.seats}
                            tickets={currentEvent.tickets || []}
                            onSelectedSeatsChange={changeSelectedSeats}
                            middleColumn={auditoriumData.middle_column}
                            eventId={eventId}
                            isGallery={isGallery}
                            galleryName={isGallery && 'gallery_name' in auditoriumData ? auditoriumData.gallery_name : undefined}
                        />
                    )}
                </WrapperColumn>

                <WrapperColumn>
                    <ColorCodeContainer>
                        {COLOR_CODES.map(({ color, label }) => (
                            <ColorCode key={label} color={color} label={label} />
                        ))}
                        <SummaryRow>
                            <p>Összesen:</p>
                            <TicketPrice>{totalPrice} Ft</TicketPrice>
                        </SummaryRow>
                        {reservationTime && (
                            <Row>
                                <strong style={{ color: '#D32F2F' }}>{reservationTime}</strong>
                            </Row>
                        )}
                    </ColorCodeContainer>

                    {complimentaryButton && <ComplimentaryCheckbox isChecked={isComplimentary} onChange={setIsComplimentary} />}

                    <ActionButton label={actionLabel} style={{ marginTop: '2rem' }} onClick={() => actionHandler(selectedSeats)} />
                </WrapperColumn>
            </WrapperRow>

            {selectedSeats.length > 0 && (
                <>
                    <SmallTitle>Kiválasztott helyek</SmallTitle>
                    <SeatSummary currentEvent={currentEvent} selectedSeats={selectedSeats} totalPrice={totalPrice} />
                </>
            )}
        </>
    )
}
